<!-- ---------------------------------------------------------------------------
  Author: Akarsh Singh
  Date: 2026-08-19
  Description: Render the RFQ priority queue as colour bands inside an assistant
    answer, using the same red / amber / green rails the supplier RFQ list screen
    paints on its own priority cards.
--------------------------------------------------------------------------- -->
<!-- ---------------------------------------------------------------------------
  Author: Akarsh Singh
  Date: 2026-06-20
  Description: Bound the launcher's vertical offset and wired pointerdown/click
    handlers so it can be dragged up/down the right edge (drag to reposition,
    tap to open/close).
--------------------------------------------------------------------------- -->
<div
  class="chatbot-wrapper"
  [class.chatbot-wrapper--open]="isOpen"
  [style.bottom.px]="fabBottom"
>
  <!-- Floating toggle button. Drag vertically along the right edge to move it;
       a plain tap still opens / closes the assistant. -->
  <button
    class="chatbot-toggle"
    (pointerdown)="onFabPointerDown($event)"
    (click)="onFabClick($event)"
    aria-label="Toggle VSMPS-AI Procurement Assistant (drag to reposition)"
    type="button"
  >
    <span class="chatbot-toggle__sweep"></span>
    <img *ngIf="!isOpen" [src]="botIconSrc" alt="" class="chatbot-toggle__icon" />
    <mat-icon *ngIf="isOpen">close</mat-icon>
  </button>

  <div
    class="chat-panel"
    *ngIf="isOpen"
    [style.width.px]="panelWidth"
    [style.height.px]="panelHeight"
  >
    <!-- Stripe-Header (matches dashboard/inbox premium pattern) -->
    <div class="chat-panel__header">
      <span class="chat-panel__sweep"></span>
      <div class="chat-panel__identity">
        <span class="chat-panel__avatar">
          <img [src]="botIconSrc" alt="" />
        </span>
        <div class="chat-panel__heading">
          <p class="chat-panel__eyebrow">MPS Copilot · Preview</p>
          <h4 class="chat-panel__title">VSMPS-AI Procurement Assistant</h4>
          <div class="chat-panel__sub">
            <span class="role-badge" [ngClass]="roleCss">{{ roleLabel }}</span>
            <span class="chat-panel__status">
              <span class="dot"></span>
              Available
            </span>
          </div>
        </div>
      </div>
    </div>

    <div class="chat-panel__body" #messageScroll>
      <div
        *ngFor="let message of messages"
        class="chat-message"
        [class.chat-message--user]="message.role === 'user'"
      >
        <div class="chat-message__avatar" *ngIf="message.role !== 'user'">
          <img [src]="botIconSrc" alt="" />
        </div>
        <div class="chat-message__avatar chat-message__avatar--user" *ngIf="message.role === 'user'">
          <mat-icon>person</mat-icon>
        </div>
        <div class="chat-message__bubble" [attr.data-tone]="message.tone">
          <p>{{ message.content }}</p>
          <div *ngIf="message.steps?.length" class="steps-list">
            <div
              class="step-item"
              *ngFor="let step of message.steps; let i = index"
              [style.animationDelay.ms]="i * 90"
            >
              <span class="step-number">{{ i + 1 }}</span>
              <span class="step-text">{{ step }}</span>
            </div>
          </div>
          <!-- RFQ priority queue, banded by how long is left to quote. The
               colours are the ones the RFQ list screen paints on its own cards,
               so a supplier reads the same signal in both places. -->
          <div *ngIf="message.rfqPriority as priority" class="rfq-priority">
            <div
              class="rfq-band"
              *ngFor="let band of priority.bands"
              [attr.data-colour]="band.colour"
            >
              <div class="rfq-band__head">
                <span class="rfq-band__dot" aria-hidden="true"></span>
                <span class="rfq-band__count">{{ band.rfqs.length }}</span>
                <span class="rfq-band__heading">{{ band.heading }}</span>
                <span class="rfq-band__note" *ngIf="band.note">· {{ band.note }}</span>
              </div>

              <div class="rfq-band__item" *ngFor="let rfq of band.rfqs">
                <div class="rfq-band__row">
                  <span class="rfq-band__ref">{{ rfq.reference }}</span>
                  <span class="rfq-band__time" *ngIf="rfq.timeLeft">
                    <mat-icon>schedule</mat-icon>{{ rfq.timeLeft }}
                  </span>
                </div>
                <div class="rfq-band__meta">
                  <span *ngIf="rfq.buyer">{{ rfq.buyer }}</span>
                  <span *ngIf="rfq.vessel">· {{ rfq.vessel }}</span>
                  <span *ngIf="rfq.port">· {{ rfq.port }}</span>
                </div>
              </div>
            </div>

            <p class="rfq-priority__closed" *ngIf="priority.quoteWindowAlreadyClosed">
              {{ priority.quoteWindowAlreadyClosed }} more can no longer be quoted — the
              window has closed.
            </p>
          </div>

          <!-- Product / vessel RFQ predictions — the rows the Suggested RFQs
               screen lists, soonest requirement first. -->
          <div *ngIf="message.rfqPredictions as predictions" class="rfq-predictions">
            <div
              class="rfq-prediction"
              *ngFor="let row of predictions.rows"
              [class.rfq-prediction--overdue]="row.overdue"
            >
              <div class="rfq-prediction__row">
                <span class="rfq-prediction__name">{{ row.productName }}</span>
                <span
                  class="rfq-prediction__confidence"
                  *ngIf="row.confidence"
                  [attr.data-level]="row.confidence"
                >{{ row.confidence }}</span>
              </div>
              <div class="rfq-prediction__meta" *ngIf="predictions.kind === 'vessel' && row.vesselName">
                <mat-icon>directions_boat</mat-icon>{{ row.vesselName }}
                <span *ngIf="row.vesselImoNumber">· IMO {{ row.vesselImoNumber }}</span>
              </div>
              <div class="rfq-prediction__meta">
                <span *ngIf="row.partNumber">Part No: {{ row.partNumber }}</span>
                <span *ngIf="row.category">· {{ row.category }}</span>
              </div>
              <div class="rfq-prediction__row rfq-prediction__forecast">
                <span class="rfq-prediction__date">
                  <mat-icon>event</mat-icon>
                  <ng-container *ngIf="row.predictedNextRequirement; else noPrediction">
                    {{ row.overdue ? 'Due since' : 'Next' }} {{ row.predictedNextRequirement | date: 'dd MMM yyyy' }}
                  </ng-container>
                  <ng-template #noPrediction>No prediction</ng-template>
                </span>
                <span class="rfq-prediction__stats">
                  <span *ngIf="row.frequency">{{ row.frequency }}</span>
                  <span *ngIf="row.averageQuantity !== null">· avg {{ row.averageQuantity }} {{ row.unit || '' }}</span>
                  <span *ngIf="row.averageGapDays">· every ~{{ row.averageGapDays }} days</span>
                </span>
              </div>
            </div>
          </div>

          <!-- One supplier's quotation versions on one RFQ — the versions panel
               of the Quote History screen, latest first. -->
          <div *ngIf="message.quoteHistory as qh" class="quote-history">
            <div class="quote-history__rfq">
              <span class="quote-history__ref">{{ qh.rfqRefNumber }}</span>
              <span class="quote-history__kind">{{ qh.rfqType === 'service' ? 'Service' : 'Product' }} RFQ</span>
            </div>
            <div class="quote-history__meta">
              <span *ngIf="qh.vesselName"><mat-icon>directions_boat</mat-icon>{{ qh.vesselName }}</span>
              <span *ngIf="qh.poNumber"><mat-icon>receipt_long</mat-icon>{{ qh.poNumber }}</span>
              <span *ngIf="qh.rfqDate"><mat-icon>event</mat-icon>{{ qh.rfqDate | date: 'dd MMM yyyy' }}</span>
              <span><mat-icon>storefront</mat-icon>{{ qh.supplierName }}</span>
            </div>

            <div
              *ngIf="qh.savings as s"
              class="quote-history__savings"
              [class.quote-history__savings--up]="s.amount < 0"
            >
              <mat-icon>{{ s.amount >= 0 ? 'trending_down' : 'trending_up' }}</mat-icon>
              <span>
                <strong>
                  {{ s.amount >= 0 ? 'Saved' : 'Increased by' }}
                  {{ s.currency }} {{ (s.amount < 0 ? -s.amount : s.amount) | number: '1.2-2' }}
                  ({{ (s.percent < 0 ? -s.percent : s.percent) | number: '1.1-1' }}%)
                </strong>
                V{{ s.fromVersion }} → V{{ s.toVersion }}{{ s.accepted ? ' (accepted)' : ' (latest)' }}
              </span>
            </div>

            <div
              class="quote-history__version"
              *ngFor="let v of qh.versions; let first = first"
              [class.quote-history__version--latest]="first"
            >
              <div class="quote-history__row">
                <span class="quote-history__vno">V{{ v.versionNo }}</span>
                <span class="quote-history__qno" *ngIf="v.quoteNumber">{{ v.quoteNumber }}</span>
                <span class="quote-history__status" *ngIf="v.status">{{ v.status }}</span>
              </div>
              <div class="quote-history__row">
                <span class="quote-history__amount">
                  {{ v.currency }} {{ v.totalAmount != null ? (v.totalAmount | number: '1.2-2') : '—' }}
                </span>
                <span
                  class="quote-history__delta"
                  *ngIf="v.changePercent !== null"
                  [class.quote-history__delta--down]="v.changePercent < 0"
                  [class.quote-history__delta--up]="v.changePercent > 0"
                >
                  {{ v.changePercent < 0 ? '▼' : v.changePercent > 0 ? '▲' : '' }}
                  {{ (v.changePercent < 0 ? -v.changePercent : v.changePercent) | number: '1.1-1' }}%
                </span>
              </div>
              <div class="quote-history__facts">
                <span *ngIf="v.submittedOn">{{ v.submittedOn | date: 'dd MMM yyyy' }}</span>
                <span *ngIf="v.deliveryDays !== null">· {{ v.deliveryDays }} days delivery</span>
                <span *ngIf="v.paymentTerms">· {{ v.paymentTerms }}</span>
              </div>
            </div>
          </div>

          <!-- Choices the assistant is asking the user to make — Product /
               Service RFQ, which RFQ a search matched, or which supplier's
               quotation history to open.
               Answered once; the buttons lock after a pick. -->
          <div *ngIf="message.options?.length" class="chat-options">
            <button
              type="button"
              class="chat-option"
              *ngFor="let option of message.options"
              [disabled]="!!message.selectedOption || isThinking"
              [class.chat-option--selected]="message.selectedOption === option.value"
              (click)="chooseOption(message, option)"
            >
              <mat-icon *ngIf="option.icon">{{ option.icon }}</mat-icon>
              <span class="chat-option__text">
                <span class="chat-option__label">{{ option.label }}</span>
                <span class="chat-option__desc" *ngIf="option.description">{{ option.description }}</span>
              </span>
            </button>
          </div>

          <div *ngIf="message.cta === 'notifications'" class="cta-row">
            <button class="link-button" (click)="openNotifications()">
              <mat-icon>notifications</mat-icon>View all notifications
            </button>
          </div>
          <div *ngIf="message.cta === 'products'" class="cta-row">
            <button class="link-button" (click)="openProducts()">
              <mat-icon>inventory_2</mat-icon>Go to product page
            </button>
          </div>
          <div *ngIf="message.link" class="cta-row">
            <button class="link-button" (click)="navigateLink(message.link)">
              <mat-icon>open_in_new</mat-icon>{{ message.link.label }}
            </button>
          </div>

          <!-- Deep links to the screens behind the data this answer used. Reuses
               the existing .cta-row / .link-button pattern so it looks identical
               to the guidance links above. -->
          <div *ngIf="message.links?.length" class="cta-row">
            <button
              class="link-button"
              *ngFor="let dataLink of message.links; trackBy: trackByLinkLabel"
              (click)="navigateLink(dataLink)"
            >
              <mat-icon>open_in_new</mat-icon>{{ dataLink.label }}
            </button>
          </div>

          <!-- Retry, only when trying again could actually work. A revoked key
               or an empty account needs an administrator, not another attempt. -->
          <div *ngIf="message.canRetry" class="cta-row">
            <button class="link-button" (click)="retryLastQuestion()">
              <mat-icon>refresh</mat-icon>Try again
            </button>
          </div>

          <!-- Something only an administrator can fix — say so plainly rather
               than letting the user retry into the same wall. -->
          <div *ngIf="message.needsAdministrator" class="admin-notice">
            <mat-icon>admin_panel_settings</mat-icon>
            <span>Your administrator needs to fix this — trying again will not help.</span>
          </div>

          <div class="chat-message__meta">
            <!-- Says where the answer came from. This is the one thing a user
                 cannot otherwise tell, and the reason the old invented fallbacks
                 were dangerous: a made-up figure looked exactly like a real one. -->
            <span class="source-tag source-tag--live" *ngIf="message.dataBacked">
              <mat-icon>verified</mat-icon>From your live data
            </span>
            <span class="source-tag source-tag--plain" *ngIf="message.degraded">
              <mat-icon>bolt</mat-icon>Quick summary
            </span>

            <!-- How long it took, and where the time went. Collapsed by default
                 so it informs without cluttering an ordinary conversation. -->
            <button
              class="timing-chip"
              type="button"
              *ngIf="message.timing"
              (click)="message.showTiming = !message.showTiming"
              [attr.aria-expanded]="!!message.showTiming"
              [attr.aria-label]="'Show how long this answer took'"
            >
              <mat-icon>schedule</mat-icon>
              {{ formatDuration(message.timing.roundTripMs) }}
              <span class="timing-chip__cold" *ngIf="message.timing.coldStart">first request</span>
              <mat-icon class="timing-chip__caret">
                {{ message.showTiming ? 'expand_less' : 'expand_more' }}
              </mat-icon>
            </button>

            <span class="timestamp">{{ message.timestamp | date: 'shortTime' }}</span>
          </div>

          <!-- The breakdown. Bars are proportional to the slowest step, so the
               thing to fix is obvious without reading the numbers. -->
          <div class="timing-panel" *ngIf="message.showTiming && message.timing">
            <div
              class="timing-row"
              *ngIf="message.timing.coldStart && message.timing.containerStartupMs"
            >
              <span class="timing-row__label">Waking the server</span>
              <span class="timing-row__bar">
                <span
                  class="timing-row__fill timing-row__fill--cold"
                  [style.width.%]="barWidthPercent(message.timing.containerStartupMs, message.timing)"
                ></span>
              </span>
              <span class="timing-row__value">{{ formatDuration(message.timing.containerStartupMs) }}</span>
            </div>

            <div
              class="timing-row"
              *ngFor="let step of message.timing.steps; trackBy: trackByStepLabel"
            >
              <span class="timing-row__label">{{ step.label }}</span>
              <span class="timing-row__bar">
                <span
                  class="timing-row__fill"
                  [style.width.%]="barWidthPercent(step.milliseconds, message.timing)"
                ></span>
              </span>
              <span class="timing-row__value">{{ formatDuration(step.milliseconds) }}</span>
            </div>

            <!-- Signing in and routing, before the assistant starts work. -->
            <div class="timing-row" *ngIf="message.timing.beforeAgentMs">
              <span class="timing-row__label">Signing you in</span>
              <span class="timing-row__bar">
                <span
                  class="timing-row__fill"
                  [style.width.%]="barWidthPercent(message.timing.beforeAgentMs, message.timing)"
                ></span>
              </span>
              <span class="timing-row__value">{{ formatDuration(message.timing.beforeAgentMs) }}</span>
            </div>

            <div class="timing-row">
              <span class="timing-row__label">Network</span>
              <span class="timing-row__bar">
                <span
                  class="timing-row__fill timing-row__fill--network"
                  [style.width.%]="barWidthPercent(message.timing.networkMs, message.timing)"
                ></span>
              </span>
              <span class="timing-row__value">{{ formatDuration(message.timing.networkMs) }}</span>
            </div>

            <div class="timing-row timing-row--total">
              <span class="timing-row__label">Total</span>
              <span class="timing-row__bar"></span>
              <span class="timing-row__value">{{ formatDuration(message.timing.roundTripMs) }}</span>
            </div>

            <p class="timing-note" *ngIf="message.timing.coldStart">
              The server had gone to sleep, so this answer paid to wake it up. The next
              questions in this conversation will be faster.
            </p>
          </div>
        </div>
      </div>

      <div class="chat-message chat-message--assistant" *ngIf="isThinking">
        <div class="chat-message__avatar">
          <img [src]="botIconSrc" alt="" />
        </div>
        <div class="chat-message__bubble typing">
          <div class="typing-text" *ngIf="thinkingMessage">{{ thinkingMessage }}</div>
          <span *ngFor="let dot of typingDots; let i = index" [class.active]="i === activeDotIndex"></span>
        </div>
      </div>
    </div>

    <div class="chat-panel__footer">
      <form class="chat-input" (ngSubmit)="submitPrompt()">
        <div class="chat-editor-wrapper">
          <div
            #editorBox
            class="chat-editor"
            contenteditable="true"
            role="textbox"
            [attr.data-placeholder]="editorPlaceholder"
            (input)="onEditorInput($event)"
            (keydown.enter)="handleEnter($event)"
          ></div>
          <button
            mat-icon-button
            color="primary"
            type="submit"
            [disabled]="promptControl.invalid || isThinking"
            aria-label="Send prompt"
            class="send-button"
          >
            <mat-icon>send</mat-icon>
          </button>
        </div>
      </form>

      <!-- Suggested prompts (always-visible essentials) -->
      <div class="quick-actions" *ngIf="quickPrompts?.length">
        <button
          class="qp-chip"
          type="button"
          *ngFor="let prompt of quickPrompts"
          (click)="runQuickPrompt(prompt.id, prompt.prompt)"
        >
          {{ prompt.label }}
        </button>
        <button
          class="qp-chip qp-chip--toggle"
          type="button"
          (click)="toggleQuickActions()"
          [class.active]="showQuickActions"
        >
          <mat-icon>{{ showQuickActions ? 'expand_less' : 'apps' }}</mat-icon>
          {{ showQuickActions ? 'Hide actions' : 'Quick actions' }}
        </button>
      </div>

      <!-- Role-aware quick actions grid -->
      <div class="quick-actions-grid" *ngIf="showQuickActions && quickActions.length">
        <button
          class="qa-card"
          type="button"
          *ngFor="let action of quickActions"
          (click)="runQuickPrompt(action.id, action.prompt)"
          [attr.title]="action.description || action.label"
        >
          <span class="qa-card__sweep"></span>
          <span class="qa-card__icon" *ngIf="action.icon">
            <mat-icon>{{ action.icon }}</mat-icon>
          </span>
          <span class="qa-card__text">
            <span class="qa-card__label">{{ action.label }}</span>
            <span class="qa-card__desc" *ngIf="action.description">{{ action.description }}</span>
          </span>
        </button>
      </div>
    </div>
  </div>
</div>
